import { cn } from "@exhousing/design-system";
import { useCallback, useLayoutEffect, useRef } from "react";

/**
 * A document rendered as a real sheet of A4 paper — correct proportions, a printed
 * margin, a soft shadow on a calm backdrop. Pass `html` (rendered document markup,
 * shown in a sandboxed iframe that grows to its content) or `children` (live React,
 * used by the letterhead designer). What you see is what prints.
 */
export function A4Sheet({
  html,
  children,
  loading,
  error,
  className,
}: {
  html?: string;
  children?: React.ReactNode;
  loading?: boolean;
  error?: string | null;
  className?: string;
}) {
  const frameRef = useRef<HTMLIFrameElement>(null);

  // Grow the iframe to its content so the paper reads as one continuous page
  // rather than a scrolling box. allow-same-origin (no scripts) lets us measure.
  const fit = useCallback(() => {
    const frame = frameRef.current;
    const doc = frame?.contentDocument;
    if (frame && doc?.body) frame.style.height = `${doc.body.scrollHeight}px`;
  }, []);

  useLayoutEffect(() => {
    if (html) fit();
  }, [html, fit]);

  return (
    <div
      className={cn(
        "flex justify-center overflow-auto rounded-xl bg-neutral-100 p-4 sm:p-8",
        className,
      )}
    >
      {/* The sheet: A4 width at 96dpi, a printed margin, paper shadow. */}
      <div
        className="w-full max-w-[794px] shrink-0 bg-white px-[8%] py-[7%] shadow-[0_8px_30px_rgba(0,0,0,0.12)] ring-1 ring-black/5"
        style={{ minHeight: error || loading ? undefined : "min(80vh, 1123px)" }}
      >
        {error ? (
          <div className="flex h-full min-h-[12rem] items-center justify-center">
            <p className="text-center text-sm text-danger">{error}</p>
          </div>
        ) : loading ? (
          <div className="space-y-3">
            <div className="h-5 w-1/3 animate-pulse rounded bg-neutral-200" />
            <div className="h-px w-full bg-neutral-200" />
            <div className="mt-8 h-3 w-1/2 animate-pulse rounded bg-neutral-200" />
            <div className="h-3 w-2/3 animate-pulse rounded bg-neutral-200" />
            <div className="mt-6 space-y-2">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="h-3 w-full animate-pulse rounded bg-neutral-200" />
              ))}
            </div>
          </div>
        ) : html !== undefined ? (
          <iframe
            ref={frameRef}
            title="Document preview"
            srcDoc={html}
            sandbox="allow-same-origin"
            onLoad={fit}
            className="block w-full border-0"
          />
        ) : (
          children
        )}
      </div>
    </div>
  );
}
